import React, { useEffect, useRef, useState } from 'react';
import * as R from 'ramda';
import { makeStyles } from '@material-ui/core/styles';
import {
  Divider,
  FormControl,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  Avatar,
  ListItemText,
  Typography,
  Tooltip,
  useTheme,
} from '@material-ui/core';
import Delete from '@material-ui/icons/Delete';
import FileIcon from '@material-ui/icons/InsertDriveFile';
import { useTranslation } from 'react-i18next';

import {
  ARR,
  isPDFFile,
  isOfficeFile,
  isFilenameAllowed,
  isSupportedAttachmentType,
} from '@utils/data-utils';
import { Dropzone } from '@components/Dropzone';
import { WarningMessage } from '@components/WarningMessage';

const useStyles = makeStyles(() => ({
  uppercase: {
    textTransform: 'uppercase',
  },
  ellipsisText: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
}));

export const FileSelector = ({
  setValue,
  files = ARR,
  valueAttribute = 'files',
  register,
  error,
  className,
  title,
  attachment = false,
  isChangeCaseComponent = false,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [filesRejected, setFilesRejected] = useState('');
  const [corruptFiles, setCorruptFiles] = useState([]);
  const [officeFiles, setOfficeFiles] = useState('');
  const dropzoneRef = useRef(null);

  const { features } = useTheme();
  const enableOfficeConversion =
    features && 'enableOfficeConversion' in features ? features.enableOfficeConversion : false;

  const clearFileStates = () => {
    setFilesRejected('');
    setCorruptFiles([]);
  };

  const attachmentTypes = features && 'attachmentTypes' in features ? features.attachmentTypes : [];

  const mimeToLabel = {
    'application/pdf': 'PDF',
    'application/xml': 'XML',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
  };

  const formattedTypes = (attachmentTypes.length > 0
    ? attachmentTypes.map((type) => mimeToLabel[type] || type)
    : ['PDF']
  ).join(', ');

  useEffect(() => {
    const updateOfficeFiles = async () => {
      const newOfficeFiles = await Promise.all(
        files.map(async (file) => ((await isOfficeFile(file)) ? file.name : null)),
      );
      setOfficeFiles(newOfficeFiles.filter(Boolean).join(', '));
    };
    updateOfficeFiles();
  }, [files]);

  useEffect(() => {
    if (error && dropzoneRef.current) {
      dropzoneRef.current.focus();
    }
  }, [error]);

  return (
    <FormControl error fullWidth className={className}>
      <Typography variant="subtitle1" component="h2" className={classes.uppercase}>
        {title}
      </Typography>
      <Divider />
      {!isChangeCaseComponent && (
        <Dropzone
          containerRef={dropzoneRef}
          maxSize={attachment ? 50 * 1024 * 1024 : 100 * 1024 * 1024} // 50MB for attachments, 100MB for others
          attachment={attachment}
          disabled={isChangeCaseComponent}
          render={async ({ acceptedFiles, namesRejectedFiles }) => {
            /*
           Validated inside the render function as validator in react-dropzone
           does not accept async validation-functions
          */
            const validatedFiles = [];
            const newCorruptFiles = [];
            const rejectedFiles = [];
            const dropZoneRejectedFileNames = await namesRejectedFiles;
            if (dropZoneRejectedFileNames) {
              rejectedFiles.push(dropZoneRejectedFileNames);
            }
            if (acceptedFiles && acceptedFiles.length) {
              await Promise.all(
                acceptedFiles.map(async (file) => {
                  if (!isFilenameAllowed(file)) {
                    rejectedFiles.push(file.name);
                  } else if (!attachment && (await isPDFFile(file))) {
                    validatedFiles.push(file);
                  } else if (!attachment && enableOfficeConversion && (await isOfficeFile(file))) {
                    validatedFiles.push(file);
                  } else if (attachment && isSupportedAttachmentType(file)) {
                    validatedFiles.push(file);
                  } else {
                    newCorruptFiles.push(file.name);
                  }
                }),
              );
            }
            setFilesRejected(rejectedFiles.join(', '));
            setCorruptFiles(newCorruptFiles);
            setValue(valueAttribute, [...files, ...validatedFiles], { shouldValidate: true });
          }}
          name={valueAttribute}
          register={register}
          inputProps={{ 'aria-describedby': error ? 'dropzone-error' : undefined }}
        />
      )}
      {error && (
        <Typography
          id="dropzone-error"
          color="error"
          style={{ marginTop: '0.5em' }}
          role="alert"
          aria-live="assertive"
        >
          {error.message}
        </Typography>
      )}
      <List dense>
        {files.map(({ name = '' }, index) => (
          <ListItem key={name}>
            <ListItemAvatar>
              <Avatar>
                <FileIcon />
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={name}
              classes={{
                primary: classes.ellipsisText,
              }}
            />
            {!isChangeCaseComponent && (
              <ListItemSecondaryAction>
                <Tooltip title={t('Delete')}>
                  <IconButton
                    edge="end"
                    aria-label={t('Delete')}
                    onClick={() => {
                      setValue(valueAttribute, R.remove(index, 1)(files));
                      clearFileStates();
                    }}
                  >
                    <Delete />
                  </IconButton>
                </Tooltip>
              </ListItemSecondaryAction>
            )}
          </ListItem>
        ))}
      </List>
      {filesRejected.length ? (
        <Typography color="error" paragraph>
          {attachment ? (
            <div>{t('Not accepted attachment', { str: filesRejected, types: formattedTypes })}</div>
          ) : (
            <div>
              {enableOfficeConversion
                ? t('Not accepted office or pdf files', { str: filesRejected })
                : t('Not accepted', { str: filesRejected })}
            </div>
          )}
        </Typography>
      ) : null}
      {corruptFiles.length ? (
        <>
          {corruptFiles.map((file) => (
            <Typography color="error" key={file} paragraph>
              {t('The file ')}
              <b>{file}</b>
              {enableOfficeConversion
                ? t('Not valid MS Office or PDF')
                : t(
                    ' is not a valid PDF file. Please make sure the file is not damaged or corrupted and try again.',
                  )}
            </Typography>
          ))}
        </>
      ) : null}

      {enableOfficeConversion && officeFiles && !attachment && (
        <WarningMessage message={t('MS Office warning', { files: officeFiles })} />
      )}
    </FormControl>
  );
};
